import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { css, keyframes, useTheme, jsx } from '@emotion/react';
import { CheckmarkBackgroundIcon } from '@paypalcorp/pp-react-icons';
import { utilStyles, ZINDEX_TOAST, getColorFamily, hasDocument, ColorProvider, KeyboardCode } from '@paypalcorp/pp-react-utils';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysColorSystemMain = tokens.sysColorSystemMain,
      sysColorSystemContrast = tokens.sysColorSystemContrast,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorSuccessContrast = tokens.sysColorSuccessContrast,
      sysMotionDurationArrive = tokens.sysMotionDurationArrive,
      sysMotionDurationDepart = tokens.sysMotionDurationDepart,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSizeIconSm = tokens.sysSizeIconSm;

  var _utilStyles = utilStyles(tokens),
      variables = _utilStyles.variables;

  var _utilStyles2 = utilStyles(tokens),
      elevationStyles = _utilStyles2.elevationStyles;

  var ppuiEaseInOut = variables.ppuiEaseInOut;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body;

  var verticalExitPosition = math("-2*".concat(sysSpaceLg));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: toast_base;\n    display: flex;\n    padding: ", ";\n    border-radius: ", ";\n    left: 0;\n    right: 0;\n    margin: auto;\n    white-space: normal;\n    ", ";\n    background-color: ", ";\n    max-width: ", ";\n    ", "\n    pointer-events: auto;\n  "])), sysSpaceLg, sysBorderRadiusLg, elevationStyles.level2, sysColorSystemMain, '30rem', textBody({
    color: sysColorSystemContrast
  }));
  var container = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: container;\n    display: flex;\n    position: fixed;\n    top: ", ";\n    z-index: ", ";\n    left: 50%;\n    transform: translateX(-50%);\n    width: calc(100% - ", " * 2);\n    visibility: hidden;\n    opacity: 0;\n    pointer-events: none;\n\n    &[aria-hidden='false'] {\n      visibility: visible;\n      opacity: 1;\n    }\n  "])), sysSpaceLg, ZINDEX_TOAST, sysSpaceLg);
  var fadeIn = keyframes(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n        0% {\n            transform: translate(\n                -50%,\n                ", "\n            );\n            opacity: 0;\n\n        }\n        100% {\n            visibility: visible;\n            transform: translate(-50%, 0);\n            opacity: 1;\n          }\n    "])), verticalExitPosition);
  var fadeOut = keyframes(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n        0% {\n            visibility: visible;\n            transform: translate(-50%, 0);\n            opacity: 1;\n        }\n        100% {\n            visibility: hidden;\n            transform: translate(\n                -50%,\n                ", "\n            );\n            opacity: 0;\n        } \n    "])), verticalExitPosition);
  var show = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: show;\n    animation: ", " ", " ", ";\n  "])), fadeIn, sysMotionDurationArrive, ppuiEaseInOut);
  var hide = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: hide;\n    animation: ", " ", " ", ";\n  "])), fadeOut, sysMotionDurationDepart, ppuiEaseInOut);
  var success = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: success;\n    background-color: ", ";\n    color: ", ";\n  "])), sysColorSuccessMain, sysColorSuccessContrast);
  var successIcon = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: success_icon;\n    margin-right: ", ";\n    height: ", ";\n\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysSpaceSm, sysSizeIconSm, sysSpaceSm);
  return {
    base: base,
    container: container,
    show: show,
    hide: hide,
    success: success,
    successIcon: successIcon
  };
}

var _excluded = ["id", "message", "isSuccess", "duration", "onClose", "direction", "className", "containerClassName", "containerRef"];
var defaultDuration = 7000;

var Toast = function (_ref) {
  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'Toast' : _ref$id,
      _ref$message = _ref.message,
      message = _ref$message === void 0 ? 'message' : _ref$message,
      _ref$isSuccess = _ref.isSuccess,
      isSuccess = _ref$isSuccess === void 0 ? false : _ref$isSuccess,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? defaultDuration : _ref$duration,
      onClose = _ref.onClose,
      direction = _ref.direction,
      className = _ref.className,
      containerClassName = _ref.containerClassName,
      containerRef = _ref.containerRef,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      showState = _useState2[0],
      setShowState = _useState2[1];

  var startY = null;
  var startX = null;
  var toastRef = useRef();

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  };

  var autoHideTimer = getWrapperCurrent(useRef());

  var handleClose = function () {
    setShowState(0);
    clearTimeout(autoHideTimer);
    startX = null;
    startY = null;
  };

  var handleKey = function (evt) {
    if (new KeyboardCode(evt.key || evt.keyCode).IS_ESC) {
      handleClose();
    }
  };
  /* istanbul ignore next */


  var handleTouchStart = function (evt) {
    //prevent from scrolling
    evt.preventDefault();
    /* istanbul ignore next */

    if (!startY && !startX) {
      var firstTouch = evt.changedTouches[0];
      startY = firstTouch.pageY;
      startX = firstTouch.pageX;
    }
  };
  /* istanbul ignore next */


  var handleTouchEnd = function (evt) {
    var endTouch = evt.changedTouches[0];
    var endX = endTouch.pageX;
    var endY = endTouch.pageY;
    var xDiff = startX - endX;
    var yDiff = startY - endY;

    if (Math.abs(yDiff) > Math.abs(xDiff)) {
      // means up/down is most significant
      if (yDiff > 0) {
        // swipe up
        handleClose();
      }
    }

    startX = null;
    startY = null;
  };

  useEffect(function () {
    if (showState < 0) {
      setShowState(1);
    }

    document.addEventListener('keydown', handleKey);
    var toast = getWrapperCurrent(toastRef); // Adding native event since evt.preventDefault (for preventing from scrolling
    // while swiping on toast up) is not possible using synthetic event
    //eslint-disable-next-line no-unused-expressions

    toast === null || toast === void 0 ? void 0 : toast.addEventListener('touchstart', handleTouchStart);
    return function () {
      document.removeEventListener('keydown', handleKey); //eslint-disable-next-line no-unused-expressions

      toast === null || toast === void 0 ? void 0 : toast.removeEventListener('touchstart', handleTouchStart);
      clearTimeout(autoHideTimer);
    };
  }, [showState]);
  /* istanbul ignore next */

  var containerCSSVal = [_styles.container, showState && _styles.show, !showState && _styles.hide];
  var toastCSSVal = [_styles.base, isSuccess && _styles.success];
  var successIcon = isSuccess && jsx(CheckmarkBackgroundIcon, {
    css: _styles.successIcon,
    size: "sm",
    "data-ppui": "true"
  });
  var colorFamily = getColorFamily(isSuccess ? 'Success' : 'Info', tokens);
  /* istanbul ignore next */

  return hasDocument() ? ReactDOM.createPortal(jsx(ColorProvider, {
    colorFamily: colorFamily,
    "data-ppui": "true"
  }, jsx("div", _extends({
    id: id,
    dir: direction,
    css: containerCSSVal,
    className: containerClassName,
    "aria-hidden": showState <= 0,
    role: "alert",
    onAnimationEnd: function onAnimatonEnd() {
      if (showState === 1) {
        var timeOut = duration > defaultDuration ? duration : defaultDuration;
        autoHideTimer = setTimeout(handleClose, timeOut);
      } else if (typeof onClose === 'function') {
        onClose();
      }
    },
    "data-ppui-info": "toast_3.4.1"
  }, rest), jsx("div", {
    ref: toastRef,
    css: toastCSSVal,
    className: className,
    onTouchEnd: handleTouchEnd,
    "data-ppui": "true"
  }, successIcon, message))), getWrapperCurrent(containerRef) || document.body) : null;
};

export { Toast, styles as toastStyles };
